import React, { useState, useEffect } from 'react';
import { signatureService } from '../services/signatureService';
import { FileText, User, Calendar, Download, Trash, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SignatureStatusTracker from './SignatureStatusTracker';

/**
 * SignatureHistory component for displaying a user's signature history
 * 
 * @param {Object} props
 * @param {string} props.documentId - ID of the document to show signatures for
 * @param {Function} props.onRefresh - Callback when signatures are refreshed
 */
const SignatureHistory = ({ documentId, onRefresh }) => {
  const [signatures, setSignatures] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('signatures');
  const { user } = useAuth();
  
  // Fetch signatures on component mount
  useEffect(() => {
    loadSignatures();
  }, [documentId]);
  
  // Load signatures from API
  const loadSignatures = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!documentId) {
        setSignatures([]);
        setRequests([]);
        setLoading(false);
        return;
      }
      
      const data = await signatureService.getDocumentSignatures(documentId);
      
      // Handle the updated API response format
      if (data.signatures) {
        setSignatures(data.signatures);
        setRequests(data.requests || []);
      } else {
        // Legacy format support
        setSignatures(data);
        setRequests([]);
      }
    } catch (error) {
      console.error('Error loading signatures:', error);
      setError('Failed to load signatures');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a signature
  const handleDeleteSignature = async (signatureId) => {
    if (!window.confirm('Are you sure you want to delete this signature?')) {
      return;
    }
    
    try {
      await signatureService.deleteSignature(signatureId);
      // Remove from local state
      setSignatures(prevSignatures => 
        prevSignatures.filter(sig => sig.id !== signatureId)
      );
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting signature:', error);
      setError('Failed to delete signature');
    }
  };
  
  // View signature details
  const viewSignatureDetails = (signatureId) => {
    window.open(`/signatures/${signatureId}`, '_blank');
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <p>{error}</p>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
      <div className="flex border-b border-gray-700 mb-4">
        <button
          className={`px-4 py-2 ${activeTab === 'signatures' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400'}`}
          onClick={() => setActiveTab('signatures')}
        >
          Signatures ({signatures.length})
        </button>
        <button
          className={`px-4 py-2 ${activeTab === 'status' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400'}`}
          onClick={() => setActiveTab('status')}
        >
          Status Tracker ({requests.length})
        </button>
      </div>
      
      {activeTab === 'signatures' ? (
        signatures.length === 0 ? (
          <div className="text-center p-6">
            <p className="text-gray-400">No signatures found for this document</p>
          </div>
        ) : (
          <div className="space-y-4">
            {signatures.map(signature => (
              <div 
                key={signature.id} 
                className="flex flex-col md:flex-row justify-between items-start md:items-center p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center mb-2 md:mb-0">
                  <div className="bg-gray-800 p-2 rounded-full mr-3">
                    <User size={18} className="text-blue-400" />
                  </div>
                  <div>
                    <div className="font-medium">{signature.userName}</div>
                    <div className="text-sm text-gray-400">{signature.userEmail}</div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center text-sm text-gray-400">
                    <Calendar size={14} className="mr-1" />
                    {new Date(signature.signedAt).toLocaleString()}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-400">
                    <FileText size={14} className="mr-1" />
                    {signature.signatureType}
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => viewSignatureDetails(signature.id)}
                      className="p-1.5 rounded-full hover:bg-gray-500"
                      title="View details"
                    >
                      <ExternalLink size={16} className="text-blue-400" />
                    </button>
                    
                    {user && (signature.userId === user.id || user.isAdmin) && (
                      <button
                        onClick={() => handleDeleteSignature(signature.id)}
                        className="p-1.5 rounded-full hover:bg-gray-500"
                        title="Delete signature"
                      >
                        <Trash size={16} className="text-red-400" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <SignatureStatusTracker 
          requests={requests} 
          documentId={documentId} 
          onRefresh={loadSignatures} 
        />
      )}
    </div>
  );
};

export default SignatureHistory; 